import React, { Fragment } from "react";
import { Tooltip } from "@vibe/core";
import { Column } from "./table.data.interface";
import { getColumnIcon } from "../../utils/columnIcons";
import ColumnValueRenderer from "./ColumnValueRenderer";
import ShareColumnButton from "./ShareColumn/ShareColumnButton";
import EditableColumnValue from "./editing/EditableColumnValue";
import type { ColumnValueType } from "../../services/monday/monday-query-interface";
import { isEmptyColumnValue } from "./editing/isEmptyColumnValue";

interface UnsharedColumnRowProps {
  column: Column;
  /** The linked item on the board this column belongs to. */
  itemId: string;
  /** Set for the current board's rows only: other boards' values stay read-only. */
  editing?: {
    boardId: number;
    onSaved: (saved: boolean, written?: ColumnValueType) => Promise<unknown> | void;
  };
}

/** One column that lives on a single board: name, the "+" to add it elsewhere, and its value. */
const UnsharedColumnRow = ({ column, itemId, editing }: UnsharedColumnRowProps) => {
  const value = (
    <ColumnValueRenderer
      otherBoardsValues
      columnData={column}
      itemIds={[Number(itemId)].filter((id) => !Number.isNaN(id))}
    />
  );

  return (
    <Fragment>
      <div className="flex items-center">
        <div className="grid grid-cols-[auto_1fr] items-center gap-[5px] min-h-[36px] p-2">
          <span className="flex items-center">{getColumnIcon(column.type)}</span>
          <div className="flex gap-[5px] items-center">
            <Tooltip content={column.title}>
              <p className="!font-figtree text-sm text-light-secondary-text-color overflow-hidden whitespace-nowrap !text-ellipsis max-w-[15ch]">
                {column.title}
              </p>
            </Tooltip>
            <ShareColumnButton title={column.title} type={column.type} />
          </div>
        </div>
      </div>
      <div className="flex items-center !font-figtree text-sm space-x-4 min-h-[36px] p-2">
        {editing ? (
          <EditableColumnValue
            columnType={column.type}
            columnId={column.columnId}
            boardId={editing.boardId}
            itemId={Number(itemId)}
            onSaved={editing.onSaved}
            isEmpty={column.type !== "file" && isEmptyColumnValue(column.value)}
          >
            {value}
          </EditableColumnValue>
        ) : (
          value
        )}
      </div>
    </Fragment>
  );
};

export default UnsharedColumnRow;
